/**
 * Turns the Branch-data diff into the rows the Summary view renders.
 *
 * policy-mod's getDifference stores the diff as two flattened `path -> value` maps
 * (`{ 'cars.0.VIN': '...', 'autoCoverage.policyLimitBIPD': '...' }`): `removedChanges` holds the old
 * value of every changed path, `addedChanges` the new one. A path missing from one side had no value
 * there. Rows are grouped by top-level node, with array items (`drivers.0`, `cars.1`, ...) as their own
 * group. Array items match by position only, because that's how the diff was computed.
 *
 * Moves between two empty states (`undefined -> false`, `null -> []`) are left out: they're data
 * corrections the preview writes, not changes staff made.
 */
import { isEqualIgnoringEmpty } from '@ourbranch/offer-utils';

const POLICY = 'Policy';
// Change on nearly every preview (policyLimitUIMPD is a comparison-only copy), so they'd top every
// Summary with noise. The Raw tab still shows them.
const HIDDEN_PATHS = ['transactionDateTime', 'startDate', 'autoCoverage.policyLimitUIMPD'];

// Listed in display order, after Policy and the other top-level nodes.
const LIST_ITEMS = {
  drivers: { noun: 'Driver', describe: ({ firstName, lastName }) => [firstName, lastName] },
  cars: { noun: 'Vehicle', describe: ({ year, make, model }) => [year, make, model] },
  trailers: { noun: 'Trailer' }
};
const LIST_ORDER = Object.keys(LIST_ITEMS);

const humanize = (key) => {
  const words = key.replace(/([a-z\d])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const displayValue = (changes, path) => {
  const value = changes[path];
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  // flat() leaves empty arrays and objects as leaf values.
  if (typeof value === 'object') {
    return '';
  }
  return String(value);
};

const changeType = (before, after) => {
  if (before === null) {
    return 'added';
  }
  return after === null ? 'removed' : 'changed';
};

const describeItem = (listItem, item) =>
  listItem?.describe && item
    ? listItem
        .describe(item)
        .map((part) => String(part ?? '').trim())
        .filter(Boolean)
        .join(' ')
    : '';

const locate = ({ path, beforeDetails, afterDetails }) => {
  const [node, second, ...rest] = path.split('.');
  if (second === undefined) {
    return { title: POLICY, rank: 0, field: path };
  }
  if (!/^\d+$/.test(second) || !rest.length) {
    return { title: humanize(node), rank: 1, node, field: [second, ...rest].join('.') };
  }
  const index = Number(second);
  const listItem = LIST_ITEMS[node];
  const title = `${listItem?.noun ?? humanize(node)} ${index + 1}`;
  const description =
    describeItem(listItem, afterDetails[node]?.[index]) || describeItem(listItem, beforeDetails[node]?.[index]);
  return {
    title: description ? `${title}: ${description}` : title,
    rank: listItem ? 2 + LIST_ORDER.indexOf(node) : 1,
    node,
    index,
    field: rest.join('.')
  };
};

export const buildBranchDiffRows = ({ addedChanges, removedChanges, beforeDetails = {}, afterDetails = {} }) =>
  [...new Set([...Object.keys(removedChanges), ...Object.keys(addedChanges)])]
    .filter((path) => !HIDDEN_PATHS.includes(path) && !isEqualIgnoringEmpty(removedChanges[path], addedChanges[path]))
    .map((path) => ({
      ...locate({ path, beforeDetails, afterDetails }),
      before: displayValue(removedChanges, path),
      after: displayValue(addedChanges, path)
    }))
    .filter(({ before, after }) => before !== after)
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        (a.node ?? '').localeCompare(b.node ?? '') ||
        (a.index ?? 0) - (b.index ?? 0) ||
        a.field.localeCompare(b.field)
    )
    .map(({ title, field, before, after }) => ({
      section: title,
      field,
      before,
      after,
      change: changeType(before, after)
    }));
